import React, {useEffect, useRef} from 'react';
import {Animated, TouchableOpacityProps} from 'react-native';
import {useTheme} from 'styled-components/native';

import {
  Container,
  Infos,
  LeftContent,
  Installment,
  Values,
  Count,
  Value,
  RightContent,
  TotalValue,
  SelectInstallment,
  ArrowRight,
  PulseWrapper,
  PulseHalo,
} from './styles';
import {Badge} from '../../../Interface/Badges/Badge';

interface Installment extends TouchableOpacityProps {
  alert?: string;
  /**
   * When true, hides the left column (Nx / labels). Use when installment
   * choice is not offered — shows only value summary without implying
   * "single payment only" (better conversion UX).
   */
  hideInstallmentColumn?: boolean;
  installmentCount: string;
  installment: string;
  totalValue: string;

  selected?: boolean;
  showArrow?: boolean;
  pulseKey?: number;
}

export function InstallmentItem({
  alert,

  hideInstallmentColumn = false,
  installmentCount,
  installment,
  totalValue,
  selected = false,
  showArrow = true,
  pulseKey,
  ...rest
}: Installment) {
  const theme = useTheme();
  const pulseAnim = useRef(new Animated.Value(0)).current;
  const [showHalo, setShowHalo] = React.useState(false);

  const showValueRow =
    !hideInstallmentColumn || !!alert;

  useEffect(() => {
    if (pulseKey === undefined || !selected) {
      return;
    }

    setShowHalo(true);
    pulseAnim.stopAnimation();
    pulseAnim.setValue(0);

    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 800,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 0,
          duration: 500,
          useNativeDriver: true,
        }),
      ]),
      { iterations: 5 },
    ).start(({ finished }) => {
      if (finished) {
        setShowHalo(false);
      }
    });
  }, [pulseKey, pulseAnim, selected]);

  const pulseOpacity = pulseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 0.22],
  });

  const pulseScale = pulseAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.05],
  });

  return (
    <PulseWrapper>
      {showHalo && (
        <PulseHalo
          pointerEvents="none"
          style={{
            opacity: pulseOpacity,
            transform: [{ scale: pulseScale }],
          }}
        />
      )}
      <Container
        activeOpacity={1}
        selected={selected}
        {...rest}
      >
        <LeftContent>
          {!hideInstallmentColumn && (
            <Installment>
              <Count>{installmentCount}x</Count>
            </Installment>
          )}
          <Infos noLeftMargin={hideInstallmentColumn}>
            {showValueRow && (
            <Values>
              {hideInstallmentColumn ? null : <Value>{installment}</Value>}
              {alert && (
                <Badge
                  text={alert}
                  containerStyle={{
                    backgroundColor: theme.colors.blue,
                    borderColor: theme.colors.blue,
                  }}
                  textStyle={{
                    color: theme.colors.shape,
                  }}
                />
              )}
            </Values>
            )}
            <TotalValue>Valor final: {totalValue}</TotalValue>
          </Infos>
        </LeftContent>
        {showArrow && (
          <RightContent>
            <SelectInstallment>
              <ArrowRight name="chevron-right" />
            </SelectInstallment>
          </RightContent>
        )}
      </Container>
    </PulseWrapper>
  );
}
